import { useRef } from "react";
import { cn } from "@/lib/utils";

// Icon tones that shine on both light and dark surfaces.
const ICON_TONES = {
  blue: "bg-primary/12 text-primary ring-1 ring-inset ring-primary/25 shadow-[0_4px_12px_-4px_hsl(var(--primary)/0.4)]",
  green: "bg-emerald-500/12 text-emerald-600 ring-1 ring-inset ring-emerald-500/25 dark:text-emerald-400 shadow-[0_4px_12px_-4px_rgb(16_185_129/0.4)]",
  amber: "bg-amber-500/12 text-amber-600 ring-1 ring-inset ring-amber-500/25 dark:text-amber-400 shadow-[0_4px_12px_-4px_rgb(245_158_11/0.4)]",
  red: "bg-rose-500/12 text-rose-600 ring-1 ring-inset ring-rose-500/25 dark:text-rose-400 shadow-[0_4px_12px_-4px_rgb(244_63_94/0.4)]",
  neutral: "bg-muted text-muted-foreground ring-1 ring-inset ring-border/60",
};

// Interactive 3D KPI card:
//  - cursor-tracked glow (radial primary halo)
//  - subtle mouse-tilt (perspective transform, ~4deg max)
//  - animated shine sweep on hover
//  - layered depth shadow tinted with primary
export function StatCard({ label, value, icon: Icon, tone = "blue", sublabel, onClick, active, testId }) {
  const clickable = typeof onClick === "function";
  const Comp = clickable ? "button" : "div";
  const ref = useRef(null);

  const onMove = (e) => {
    const node = ref.current;
    if (!node) return;
    const rect = node.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    // Glow position
    node.style.setProperty("--x", `${x}px`);
    node.style.setProperty("--y", `${y}px`);
    // 3D tilt (max ±4 deg)
    const rx = ((y / rect.height) - 0.5) * -6;
    const ry = ((x / rect.width) - 0.5) * 8;
    node.style.setProperty("--rx", `${rx.toFixed(2)}deg`);
    node.style.setProperty("--ry", `${ry.toFixed(2)}deg`);
  };
  const onLeave = () => {
    const node = ref.current;
    if (!node) return;
    node.style.setProperty("--rx", "0deg");
    node.style.setProperty("--ry", "0deg");
  };

  return (
    <Comp
      ref={ref}
      type={clickable ? "button" : undefined}
      onClick={onClick}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
      data-testid={testId}
      className={cn(
        "card-3d kpi-glow group p-5 text-left focus-ring",
        clickable && "cursor-pointer",
        active && "!border-primary/70 ring-2 ring-primary/25"
      )}
      style={{
        transform:
          "perspective(900px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) translateZ(0)",
      }}
    >
      <div className="flex items-start justify-between">
        <p className="overline select-none" translate="no">{label}</p>
        {Icon && (
          <div
            className={cn(
              "flex h-10 w-10 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-[1.08] group-hover:-translate-y-0.5",
              ICON_TONES[tone] || ICON_TONES.blue
            )}
            style={{ transform: "translateZ(20px)" }}
          >
            <Icon className="h-4 w-4" strokeWidth={2} />
          </div>
        )}
      </div>
      <p
        className="font-display mt-5 text-4xl font-semibold tracking-tight text-foreground tabular-nums"
        style={{ transform: "translateZ(24px)" }}
      >
        {value}
      </p>
      {sublabel && (
        <p className="mt-1 text-xs text-muted-foreground" style={{ transform: "translateZ(12px)" }}>
          {sublabel}
        </p>
      )}
    </Comp>
  );
}
